import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { HomeworkSummary } from "@edu/shared";
import * as api from "../../../lib/api.ts";
import { HomeworkRow } from "./HomeworkRow.tsx";

/**
 * Просроченное над списком.
 *
 * Зачем блок. Требование владельца дословно: не видно, что просрочено и у кого.
 * Фильтр «Просрочено» на это отвечает, но только тому, кто догадался его нажать,
 * а вопрос «что горит» человек задаёт себе каждый раз, когда открывает хаб.
 * Поэтому просроченное показано СРАЗУ, а фильтр остаётся способом посмотреть
 * список целиком.
 *
 * ⚠️ Отдельный запрос, а не отбор среди приехавших строк. Страница курсорная и
 * отдаёт свежие работы, а просрочка живёт в старых: клиентский отбор
 * систематически показывал бы пусто ровно там, где блок нужен.
 *
 * ⚠️ Показывается ТОЛЬКО при фильтре «Все» (решает вызывающий). При «Просрочено»
 * это дубль всего списка, при «Выполнено» противоречие.
 *
 * ⚠️ Отказ запроса не роняет экран и не рисует красную строку: основной список
 * уже приехал и полезен, а сообщение над ним сказали бы про него. Блок просто
 * не появляется.
 */

/** Сколько строк показываем. Остальное за ссылкой: это врезка, а не список. */
const SHOWN = 5;

export function OverdueBlock(props: {
  studentUserId: string | null;
  /** Тот же разрез, что у основного списка: колонка имени исчезает у всех строк. */
  hideCounterpart: boolean;
  backTo: string;
  /** Адрес того же разреза с фильтром «Просрочено». */
  allHref: string;
  now: number;
}) {
  const { studentUserId } = props;
  const [rows, setRows] = useState<HomeworkSummary[] | null>(null);
  /** Всего просроченных по ВСЕЙ выборке. `null` означает «сервер не прислал». */
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setTotal(null);
    void (async () => {
      try {
        // Просим на одну строку больше, чем покажем: этого хватает, чтобы узнать
        // про «есть ещё», и не тянет по каналу 128 кбит/с страницу из двадцати
        // пяти строк ради пяти показанных
        const res = await api.listHomework({
          state: "overdue",
          studentUserId,
          limit: SHOWN + 1,
        });
        if (!alive) return;
        setRows(res.homeworks);
        setTotal(res.counts?.overdue ?? null);
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [studentUserId]);

  if (rows === null || rows.length === 0) return null;

  const shown = rows.slice(0, SHOWN);
  const more = rows.length > SHOWN || (total !== null && total > SHOWN);

  return (
    <ul className={props.hideCounterpart ? "list list--hw-solo" : "list list--hw"} data-rows="page">
      {/*
        Число берётся из счётчиков сервера, а не из длины этого списка: здесь
        приехало шесть строк на пять показанных, и «Просрочено · 6» у
        преподавателя с двадцатью просроченными было бы враньём. Старый сервер
        счётчиков не присылает, и тогда заголовок остаётся без числа: без числа
        честнее, чем с неверным
      */}
      <li className="list__group">Просрочено{total === null ? "" : ` · ${total}`}</li>

      {shown.map((h) => (
        <HomeworkRow
          key={h.id}
          homework={h}
          hideCounterpart={props.hideCounterpart}
          backTo={props.backTo}
          now={props.now}
        />
      ))}

      {more && (
        <li className="list__foot">
          <Link className="textlink" to={props.allHref}>
            показать все просроченные
          </Link>
        </li>
      )}
    </ul>
  );
}
